import React, { useState, useMemo, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { ValidationRule, ButtonType } from 'devextreme-react/common';
import { Popup } from 'devextreme-react/popup';
import { PopupPosition } from '../common/PopupPositions';
import { Toast } from 'devextreme-react/toast';
import { Form, Label, FormRef, GroupItem, SimpleItem, ButtonItem, EmptyItem } from 'devextreme-react/form';
import { LoadPanel } from 'devextreme-react/load-panel';
import 'devextreme-react/text-area';

import LabelTemplate from '../label-template/LabelTemplate';
import DatabaseForm, { DatabaseFormHandle } from '../database-form/DatabaseForm';

import { formatMessage } from "devextreme/localization";

import DatabaseApi from "./../../api/DatabaseApi";

import { JobStatusError } from '../../classes/consts/JobStatus';

import DatabaseReferenceIdAndKeyDto from '../../classes/dtos/DatabaseReferenceIdAndKeyDto';

import DatabaseSelectionDto from '../../classes/dtos/datastructuremodule/DatabaseSelectionDto';


interface DatabaseSelectionFormProps {
    DataSourceId: number;
    WithCreate: boolean;
    SetDatabaseSelected(value: DatabaseSelectionDto): void;
}

export interface DatabaseSelectionFormHandle {
    Open: (databaseReferenceId: number) => void;
}

const DatabaseSelectionForm = forwardRef<DatabaseSelectionFormHandle, DatabaseSelectionFormProps>(({ DataSourceId, WithCreate, SetDatabaseSelected }, ref) => {

    const position = { of: '#root' };

    const databaseFormHandleRef = useRef<DatabaseFormHandle>(null);

    const [refreshInProgress, setRefreshInProgress] = useState(false);

    const [databaseSelectionDto, setDatabaseSelectionDto] = useState<DatabaseSelectionDto>(new DatabaseSelectionDto(null));

    const [isOpen, setIsOpen] = useState(false);

    const [toastConfig, setToastConfig] = useState({
        isVisible: false,
        type: 'info',
        message: '',
        withRedirect: false
    } as {
        isVisible: boolean,
        type: 'info' | 'error' | 'success' | 'warning',
        message: string,
        withRedirect: boolean
    });

    const onHiding = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false,
        });

    }, [toastConfig]);

    useImperativeHandle(ref, () => ({
        Open(databaseReferenceId: number) {
            setIsOpen(true);

            DatabaseApi.GetDatabasesAsync(DataSourceId, false)
                .then(getDatabasesResult => {
                    let allDatabases = getDatabasesResult.Result.Databases
                        .toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);
                    let current = allDatabases.find((e) => e.Id === databaseReferenceId) ?? null;
                    setDatabases(allDatabases);
                    setDatabaseSelectionDto(new DatabaseSelectionDto(current));
                });
        }
    }));

    const formRef = useRef<FormRef>(null);

    const [databases, setDatabases] = useState<DatabaseReferenceIdAndKeyDto[] | null>(null);

    const databaseEditorOptions = {
        items: databases,
        searchEnabled: true,
        displayExpr: "Name",
        buttons: [
            "dropDown",
            {
                name: 'edit',
                location: 'after',
                options: {
                    stylingMode: 'text',
                    icon: 'refresh',
                    onClick: async () => {
                        setRefreshInProgress(true);
                        let getDatabasesResult = await DatabaseApi.GetDatabasesAsync(DataSourceId, true);

                        if (getDatabasesResult.IsFailed()) {
                            setRefreshInProgress(false);
                            setToastConfig({
                                ...toastConfig,
                                isVisible: true,
                                type: 'error',
                                message: getDatabasesResult.ErrorMessage,
                                withRedirect: false
                            });
                            return;
                        }

                        if (getDatabasesResult.Result.Accepted === false) {
                            setRefreshInProgress(false);
                            setToastConfig({
                                ...toastConfig,
                                isVisible: true,
                                type: 'error',
                                message: formatMessage('RefreshDatabasesNotAccepted'),
                                withRedirect: false
                            });
                            return;
                        }

                        if (getDatabasesResult.Result.Status === JobStatusError) {
                            setRefreshInProgress(false);
                            setToastConfig({
                                ...toastConfig,
                                isVisible: true,
                                type: 'error',
                                message: formatMessage('RefreshDatabasesError'),
                                withRedirect: false
                            });
                            return;
                        }

                        let allDatabases = getDatabasesResult.Result.Databases
                            .toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);
                        let current = allDatabases.find((e) => e.Id === databaseSelectionDto.Database?.Id) ?? null;
                        setDatabases(allDatabases);
                        setDatabaseSelectionDto(new DatabaseSelectionDto(current));
                        setRefreshInProgress(false);
                        setToastConfig({
                            ...toastConfig,
                            isVisible: true,
                            type: 'success',
                            message: formatMessage('RefreshDatabasesSuccess'),
                            withRedirect: false
                        });
                    }
                },
            }]
    };

    const validationRules: {
        database: ValidationRule[]
    } = {
        database: [
            { type: 'required', message: 'Database is required.' },
        ]
    };

    function cancel(): void {
        setIsOpen(false);
    };

    const popupAttributes = useMemo(() => {
        return {
            id: 'elementId',
            class: 'class-name-popup'
        }
    }, []);

    const handleSubmit = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        SetDatabaseSelected(databaseSelectionDto);
        setIsOpen(false);
    };

    function createDatabase(): void {
        if (databaseFormHandleRef.current !== null) {
            databaseFormHandleRef.current.Open(DataSourceId);
        }
    };

    const handleDatabaseCreated = async (name: string) => {
        // The database is now created in the source: force a refresh to discover it.
        let getDatabasesResult = await DatabaseApi.GetDatabasesAsync(DataSourceId, true);

        if (getDatabasesResult.IsFailed() || !getDatabasesResult.Result.Accepted) {
            return;
        }

        let allDatabases = [...getDatabasesResult.Result.Databases].sort((a, b) => a.Name.localeCompare(b.Name, "fr", { sensitivity: "base" }));

        let current = allDatabases.find((e) => e.Name === name) ?? null;

        setDatabases(allDatabases);
        setDatabaseSelectionDto(new DatabaseSelectionDto(current));
    };

    const createDatabaseButtonOptions = {
        text: 'Create a new database',
        type: 'outlined' as ButtonType,
        useSubmitBehavior: false,
        width: '100%',
        icon: 'plus',
        onClick: createDatabase
    };

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'outlined' as ButtonType,
        useSubmitBehavior: false,
        width: '100%',
        onClick: cancel
    };

    const confirmButtonOptions = {
        text: 'Confirm',
        type: 'default' as ButtonType,
        useSubmitBehavior: true,
        width: '100%'
    };

    return (
        <React.Fragment>
            <Popup
                wrapperAttr={popupAttributes}
                visible={isOpen}
                onHiding={cancel}
                dragEnabled={false}
                hideOnOutsideClick={false}
                showCloseButton={true}
                showTitle={true}
                title="Choose your database"
                container=".dx-viewport"
                width={600}
                height="auto"
                position={PopupPosition}
            >
                <form onSubmit={handleSubmit}>
                    <Form
                        ref={formRef}
                        formData={databaseSelectionDto}
                        showColonAfterLabel={true}
                        showValidationSummary={false}
                        validationGroup="databaseSelectionDtoData"
                        colCount={2}
                    >
                        <GroupItem colSpan={2}>
                            <SimpleItem dataField="Database" editorType="dxSelectBox" editorOptions={databaseEditorOptions} validationRules={validationRules.database}>
                                <Label render={LabelTemplate('product')} />
                            </SimpleItem>
                        </GroupItem>
                        {WithCreate && <GroupItem colSpan={2}>
                            <div style={{ width: '100%', height: 36, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 12, display: 'inline-flex' }}>
                                <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 4, display: 'inline-flex' }}>
                                    <div style={{ width: 20, height: 0, opacity: 0.50, border: '1px #5A5A65 solid' }}></div>
                                    <div style={{ color: '#9393A2', fontSize: 10, fontFamily: 'Manrope', fontWeight: '600', textTransform: 'uppercase', lineHeight: '13px', letterSpacing: 1, wordWrap: 'break-word' }}>OR</div>
                                    <div style={{ width: 20, height: 0, opacity: 0.50, border: '1px #5A5A65 solid' }}></div>
                                </div>
                            </div>
                        </GroupItem>}
                        {WithCreate && <GroupItem colSpan={2}>
                            <ButtonItem buttonOptions={createDatabaseButtonOptions} />
                        </GroupItem>}
                        <EmptyItem colSpan={2} />
                        <GroupItem>
                            <ButtonItem buttonOptions={cancelButtonOptions} />
                        </GroupItem>
                        <GroupItem>
                            <ButtonItem buttonOptions={confirmButtonOptions} />
                        </GroupItem>
                    </Form>
                </form>
            </Popup>
            <LoadPanel
                position={position}
                shadingColor="rgba(0,0,0,0.4)"
                visible={refreshInProgress}
            />
            <DatabaseForm ref={databaseFormHandleRef} DatabaseAdded={handleDatabaseCreated} />
            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                type={toastConfig.type}
                onHiding={onHiding}
                displayTime={toastConfig.type === 'success' ? 1000 : 3000}
            />
        </React.Fragment>
    );
});

export default DatabaseSelectionForm;